/*!
 * motion.js - End-to-End Store Builder motion layer
 * Framework-free ES2019. No build step. Progressive enhancement only.
 *
 * Markup contract:  data-motion="<pattern>" plus data-motion-<option> attributes.
 * Level contract:   <html data-motion-level="subtle|expressive|cinematic">  (default: subtle)
 * Scroller:         <html data-motion-scroller=".page-wrapper">             (default: window)
 *
 * Rules this file must never break:
 *  - Content is fully visible with JS off, with a failed CDN load, and under reduced motion.
 *  - Only transform / opacity / clip-path are animated. Never width/height/top/left/margin.
 *  - Initial hidden states come ONLY from the .motion-ready class (see motion.css).
 *  - Libraries load lazily, single-flight, with a timeout, and only if a pattern on the page needs them.
 *
 * Public API: window.StoreMotion = { init, destroy, refresh, level, scroller, version }
 * (window.Motion belongs to the Motion library - do not collide with it.)
 */
(function (win, doc) {
  'use strict';

  if (!win.Promise || !win.IntersectionObserver) return; // nothing hidden, nothing animated

  var VERSION = '1.0.0';
  var HTML = doc.documentElement;

  /* ---------------------------------------------------------------- libraries */

  var CDN = {
    gsap: 'https://cdn.jsdelivr.net/npm/gsap@3.15.0/dist/gsap.min.js',
    scrolltrigger: 'https://cdn.jsdelivr.net/npm/gsap@3.15.0/dist/ScrollTrigger.min.js',
    splittext: 'https://cdn.jsdelivr.net/npm/gsap@3.15.0/dist/SplitText.min.js',
    motion: 'https://cdn.jsdelivr.net/npm/motion@13.4.0/dist/motion.js',
    lenis: 'https://cdn.jsdelivr.net/npm/lenis@1.3.26/dist/lenis.min.js',
    embla: 'https://cdn.jsdelivr.net/npm/embla-carousel@8.6.0/embla-carousel.umd.js'
  };
  var GLOBALS = {
    gsap: 'gsap', scrolltrigger: 'ScrollTrigger', splittext: 'SplitText',
    motion: 'Motion', lenis: 'Lenis', embla: 'EmblaCarousel'
  };
  var DEPS = { scrolltrigger: ['gsap'], splittext: ['gsap'] };
  var LOAD_TIMEOUT = 8000;
  var BOOT_TIMEOUT = 3000;

  var loading = {}; // single-flight cache

  function inject(src, globalName) {
    return new Promise(function (resolve, reject) {
      var s = doc.createElement('script');
      var timer = win.setTimeout(function () { done(new Error('timeout ' + src)); }, LOAD_TIMEOUT);
      var settled = false;
      function done(err) {
        if (settled) return;
        settled = true;
        win.clearTimeout(timer);
        s.onload = null; s.onerror = null;
        if (err) reject(err);
        else if (win[globalName]) resolve(win[globalName]);
        else reject(new Error('no global ' + globalName));
      }
      s.src = src; s.async = true; s.crossOrigin = 'anonymous';
      s.onload = function () { done(null); };
      s.onerror = function () { done(new Error('failed ' + src)); };
      (doc.head || HTML).appendChild(s);
    });
  }

  function lib(key) {
    if (loading[key]) return loading[key];
    if (win[GLOBALS[key]]) { loading[key] = Promise.resolve(win[GLOBALS[key]]); return loading[key]; }
    var deps = DEPS[key] ? Promise.all(DEPS[key].map(lib)) : Promise.resolve();
    loading[key] = deps.then(function () { return inject(CDN[key], GLOBALS[key]); });
    return loading[key];
  }

  function libs(keys) {
    if (!keys || !keys.length) return Promise.resolve();
    return Promise.all(keys.map(lib)).then(function () {
      if (win.gsap && win.ScrollTrigger && !win.ScrollTrigger.__smRegistered) {
        win.gsap.registerPlugin(win.ScrollTrigger); win.ScrollTrigger.__smRegistered = true;
      }
      if (win.gsap && win.SplitText && !win.SplitText.__smRegistered) {
        win.gsap.registerPlugin(win.SplitText); win.SplitText.__smRegistered = true;
      }
    });
  }

  /* ------------------------------------------------------------ level / gating */

  var RANK = { subtle: 1, expressive: 2, cinematic: 3 };
  var DUR_MUL = { subtle: 1, expressive: 1.12, cinematic: 1.25 };
  var STAG_MUL = { subtle: 1, expressive: 1.15, cinematic: 1.3 };

  // Nearest-allowed degradation. null = do not animate (content just stays put).
  var DEGRADE = {
    'split-headline': 'reveal-up',
    'parallax-media': 'reveal-up',
    'image-mask-reveal': 'reveal-up',
    'pin-scrub': 'parallax-media',
    'marquee': null,
    'magnetic-button': null,
    'sticky-progress': null,
    'smooth-scroll': null
  };
  // Disabled on small / coarse-pointer devices for scroll-jank reasons.
  var MOBILE_OFF = {
    'magnetic-button': null,
    'parallax-media': 'reveal-up',
    'pin-scrub': 'reveal-up',
    'smooth-scroll': null
  };

  function level() {
    var l = (HTML.getAttribute('data-motion-level') || 'subtle').toLowerCase();
    return RANK[l] ? l : 'subtle';
  }
  function isMobile() {
    return win.innerWidth < 768 || (win.matchMedia && win.matchMedia('(pointer: coarse)').matches);
  }
  function reduced() {
    return !!(win.matchMedia && win.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function resolve(name) {
    var lvl = level(), mobile = isMobile(), guard = 0;
    while (name && guard++ < 6) {
      if (!PATTERNS[name]) return null;
      if (mobile && Object.prototype.hasOwnProperty.call(MOBILE_OFF, name)) { name = MOBILE_OFF[name]; continue; }
      if (RANK[PATTERNS[name].level] > RANK[lvl]) { name = DEGRADE[name]; continue; }
      return name;
    }
    return null;
  }

  /* ------------------------------------------------------------------ options */

  var EASES = {
    'out': 'cubic-bezier(0.22,0.61,0.36,1)',
    'power2.out': 'cubic-bezier(0.22,0.61,0.36,1)',
    'power3.out': 'cubic-bezier(0.22,1,0.36,1)',
    'power4.out': 'cubic-bezier(0.16,1,0.3,1)',
    'expo.out': 'cubic-bezier(0.16,1,0.3,1)',
    'back.out': 'cubic-bezier(0.34,1.56,0.64,1)',
    'linear': 'linear',
    'none': 'linear'
  };

  // Accepts "600", "600ms", "0.6s", "0.6". Bare numbers <= 10 are read as seconds. Returns ms.
  function time(value, fallback) {
    if (value === null || value === undefined || value === '') return fallback;
    var str = String(value).trim();
    var num = parseFloat(str);
    if (isNaN(num)) return fallback;
    if (/ms$/.test(str)) return num;
    if (/s$/.test(str)) return num * 1000;
    return num <= 10 ? num * 1000 : num;
  }
  function attr(el, name, fallback) {
    var v = el.getAttribute('data-motion-' + name);
    return v === null || v === '' ? fallback : v;
  }
  function num(el, name, fallback) {
    var v = parseFloat(attr(el, name, NaN));
    return isNaN(v) ? fallback : v;
  }
  function bool(el, name) {
    var v = el.getAttribute('data-motion-' + name);
    return v !== null && v !== 'false';
  }

  function opts(el) {
    var lvl = level();
    var explicitDelay = el.getAttribute('data-motion-delay');
    var o = {
      delay: explicitDelay !== null ? time(explicitDelay, 0) : (el.__smAutoDelay || 0),
      duration: time(attr(el, 'duration', null), 600) * DUR_MUL[lvl],
      stagger: time(attr(el, 'stagger', null), 70) * STAG_MUL[lvl],
      ease: attr(el, 'ease', 'power3.out'),
      start: attr(el, 'start', 'top 85%'),
      scrub: attr(el, 'scrub', null),
      strength: num(el, 'strength', NaN),
      distance: num(el, 'distance', NaN),
      target: attr(el, 'target', null)
    };
    o.cssEase = EASES[o.ease] || (/^cubic-bezier|^linear|^ease/.test(o.ease) ? o.ease : EASES.out);
    o.scrubValue = o.scrub === null ? false : (o.scrub === 'true' || o.scrub === '' ? true : parseFloat(o.scrub) || true);
    return o;
  }

  /* ------------------------------------------------------- scroller resolution */

  var scroller = null; // null => window

  function resolveScroller() {
    var sel = HTML.getAttribute('data-motion-scroller');
    if (!sel) return null;
    var el = doc.querySelector(sel);
    if (!el) return null;
    var oy = win.getComputedStyle(el).overflowY;
    // Horizon only makes .page-wrapper scrollable at >= 990px; below that the window scrolls.
    if ((oy === 'auto' || oy === 'scroll') && el.scrollHeight > el.clientHeight + 1) return el;
    return null;
  }
  function stConf(el, o, extra) {
    var c = { trigger: el, start: o.start };
    if (scroller) c.scroller = scroller;
    for (var k in extra) if (Object.prototype.hasOwnProperty.call(extra, k)) c[k] = extra[k];
    return c;
  }

  /* ---------------------------------------------------------- shared utilities */

  var instances = [];   // { root, kills: [fn] }
  var bound = new (win.WeakSet || function () { this.add = function () {}; this.has = function () { return false; }; })();
  var booted = false;

  function register(root, kill) {
    for (var i = 0; i < instances.length; i++) {
      if (instances[i].root === root) { instances[i].kills.push(kill); return; }
    }
    instances.push({ root: root, kills: [kill] });
  }

  // Make an element (and any pre-hidden children) visible and inert. Every failure path ends here.
  function release(el) {
    el.classList.add('motion-done');
    var kids = el.children, i;
    for (i = 0; i < kids.length; i++) kids[i].classList.add('motion-done');
  }

  // One-shot viewport trigger, honouring data-motion-start as a rootMargin-ish percentage.
  function onEnter(el, o, fn) {
    var pct = 15;
    var m = /(\d+(?:\.\d+)?)%/.exec(o.start || '');
    if (m) pct = Math.max(0, 100 - parseFloat(m[1]));
    var io = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting) { io.disconnect(); fn(); return; }
      }
    }, { root: scroller || null, rootMargin: '0px 0px -' + pct + '% 0px', threshold: 0 });
    io.observe(el);
    return function () { io.disconnect(); };
  }

  // Safety net for the two GSAP patterns that start from a hidden state: if the ScrollTrigger has
  // fired but the tween never advanced (frozen rAF, a throttled background tab, a ticker that never
  // started), clear the inline props so the content shows rather than staying invisible.
  function failsafe(anim, targets, ms) {
    var t = win.setTimeout(function () {
      var st = anim.scrollTrigger;
      if (st && st.progress > 0 && anim.progress() === 0 && win.gsap) win.gsap.set(targets, { clearProps: 'all' });
    }, ms);
    return function () { win.clearTimeout(t); };
  }

  function transition(el, o, extraDelay) {
    var d = Math.round(o.duration) + 'ms';
    var wait = Math.round(o.delay + (extraDelay || 0)) + 'ms';
    el.style.transition = 'opacity ' + d + ' ' + o.cssEase + ' ' + wait +
                          ', transform ' + d + ' ' + o.cssEase + ' ' + wait +
                          ', clip-path ' + d + ' ' + o.cssEase + ' ' + wait;
  }

  /* ----------------------------------------------------------------- patterns */

  var PATTERNS = {

    /* ---- zero-dependency engine (IntersectionObserver + CSS transitions + rAF) ---- */

    'reveal-up': {
      level: 'subtle', libs: [],
      run: function (el, o) {
        return onEnter(el, o, function () { transition(el, o); el.classList.add('motion-done'); });
      }
    },

    'stagger-children': {
      level: 'subtle', libs: [],
      run: function (el, o) {
        var kids = el.querySelectorAll(':scope > *');
        return onEnter(el, o, function () {
          for (var i = 0; i < kids.length; i++) transition(kids[i], o, i * o.stagger);
          el.classList.add('motion-done');
        });
      }
    },

    'count-up': {
      level: 'subtle', libs: [],
      run: function (el, o) {
        var raw = (el.textContent || '').trim();
        var match = /-?[\d][\d.,\s]*/.exec(raw);
        if (!match) { release(el); return function () {}; }
        var token = match[0];
        var prefix = raw.slice(0, match.index);
        var suffix = raw.slice(match.index + token.length);
        var decimals = (/[.,](\d+)$/.exec(token.replace(/\s/g, '')) || [null, ''])[1];
        var grouped = /[,\s]\d{3}/.test(token);
        var to = parseFloat(attr(el, 'to', token.replace(/[^\d.-]/g, '')));
        var from = parseFloat(attr(el, 'from', 0));
        if (isNaN(to)) { release(el); return function () {}; }
        var places = decimals && decimals.length < 3 ? decimals.length : 0;
        var raf = 0;
        function fmt(v) {
          var s = grouped ? v.toLocaleString(undefined, { minimumFractionDigits: places, maximumFractionDigits: places })
                          : v.toFixed(places);
          return prefix + s + suffix;
        }
        el.classList.add('motion-done');
        var stop = onEnter(el, o, function () {
          var t0 = 0, dur = o.duration + o.delay, wait = o.delay;
          el.setAttribute('aria-label', fmt(to));
          raf = win.requestAnimationFrame(function step(now) {
            if (!t0) t0 = now;
            var p = Math.min(1, Math.max(0, (now - t0 - wait) / (dur - wait)));
            var eased = 1 - Math.pow(1 - p, 3);
            el.textContent = fmt(from + (to - from) * eased);
            if (p < 1) raf = win.requestAnimationFrame(step);
            else el.textContent = fmt(to);
          });
        });
        return function () { stop(); if (raf) win.cancelAnimationFrame(raf); el.textContent = raw; };
      }
    },

    'atc-feedback': {
      level: 'subtle', libs: [],
      run: function (el, o) {
        el.classList.add('motion-done');
        if (!el.querySelector('.motion-atc__check')) {
          var check = doc.createElement('span');
          check.className = 'motion-atc__check';
          check.setAttribute('aria-hidden', 'true');
          el.appendChild(check);
          el.classList.add('motion-atc');
        }
        var hold = Math.max(900, o.duration * 2);
        var timer = 0;
        function fire() {
          el.classList.remove('motion-atc--success');
          void el.offsetWidth; // restart the keyframes
          el.classList.add('motion-atc--success');
          win.clearTimeout(timer);
          timer = win.setTimeout(function () { el.classList.remove('motion-atc--success'); }, hold);
        }
        function onClick() { lastAtc = el; }
        // The CRO blocks dispatch cro:cart:updated on document after a successful /cart/add.js.
        function onCart(e) {
          var d = e && e.detail ? e.detail : null;
          // Only a real add earns the success state; quantity edits and theme-side re-emits do not.
          if (d && ((d.action && d.action !== 'add') || d.source === 'theme-adapter')) return;
          var target = d && (d.button || (d.form && d.form.querySelector('[data-motion="atc-feedback"]')));
          if (target ? target === el : lastAtc === el) fire();
        }
        el.addEventListener('click', onClick);
        doc.addEventListener('cro:cart:updated', onCart);
        return function () {
          win.clearTimeout(timer);
          el.removeEventListener('click', onClick);
          doc.removeEventListener('cro:cart:updated', onCart);
          el.classList.remove('motion-atc--success');
        };
      }
    },

    'marquee': {
      level: 'expressive', libs: [],
      run: function (el, o) {
        var track = el.querySelector('.motion-marquee__track');
        if (!track) { release(el); return function () {}; }
        el.classList.add('motion-done');
        if (!track.hasAttribute('data-motion-cloned')) {
          var clone = track.cloneNode(true);
          clone.setAttribute('aria-hidden', 'true');
          clone.setAttribute('data-motion-clone', '');
          track.parentNode.appendChild(clone);
          track.setAttribute('data-motion-cloned', '');
        }
        var secs = (time(attr(el, 'duration', null), 30000)) / 1000;
        el.style.setProperty('--motion-marquee-duration', secs + 's');
        if (bool(el, 'reverse')) el.classList.add('motion-marquee--reverse');
        el.classList.add('motion-marquee--run');
        var io = new IntersectionObserver(function (entries) {
          el.classList.toggle('motion-marquee--paused', !entries[0].isIntersecting);
        }, { root: scroller || null });
        io.observe(el);
        return function () {
          io.disconnect();
          el.classList.remove('motion-marquee--run', 'motion-marquee--paused');
          var c = el.querySelector('[data-motion-clone]');
          if (c) c.parentNode.removeChild(c);
          track.removeAttribute('data-motion-cloned');
        };
      }
    },

    /* --------------------------- Embla ---------------------------- */

    'carousel': {
      level: 'subtle', libs: ['embla'],
      run: function (el, o) {
        var viewport = el.querySelector('.motion-carousel__viewport');
        if (!viewport) { release(el); return function () {}; }
        el.classList.add('motion-done');
        var slides = el.querySelectorAll('.motion-carousel__slide');
        viewport.setAttribute('role', 'region');
        viewport.setAttribute('aria-roledescription', 'carousel');
        if (!viewport.hasAttribute('tabindex')) viewport.setAttribute('tabindex', '0');
        if (!viewport.hasAttribute('aria-label')) viewport.setAttribute('aria-label', attr(el, 'label', 'Carousel'));
        for (var i = 0; i < slides.length; i++) {
          slides[i].setAttribute('aria-roledescription', 'slide');
          slides[i].setAttribute('aria-label', (i + 1) + ' of ' + slides.length);
        }
        var embla = win.EmblaCarousel(viewport, {
          loop: bool(el, 'loop'),
          align: attr(el, 'align', 'start'),
          slidesToScroll: num(el, 'slides-to-scroll', 1),
          containScroll: 'trimSnaps',
          duration: Math.round(o.duration / 25)
        });
        var prev = el.querySelector('[data-motion-carousel-prev]');
        var next = el.querySelector('[data-motion-carousel-next]');
        var dotsBox = el.querySelector('[data-motion-carousel-dots]');
        var dots = [];
        function goPrev() { embla.scrollPrev(); }
        function goNext() { embla.scrollNext(); }
        if (prev) { prev.addEventListener('click', goPrev); if (!prev.getAttribute('aria-label')) prev.setAttribute('aria-label', 'Previous slide'); }
        if (next) { next.addEventListener('click', goNext); if (!next.getAttribute('aria-label')) next.setAttribute('aria-label', 'Next slide'); }
        if (dotsBox) {
          dotsBox.innerHTML = '';
          embla.scrollSnapList().forEach(function (_, idx) {
            var b = doc.createElement('button');
            b.type = 'button';
            b.className = 'motion-carousel__dot';
            b.setAttribute('aria-label', 'Go to slide ' + (idx + 1));
            b.addEventListener('click', function () { embla.scrollTo(idx); });
            dotsBox.appendChild(b);
            dots.push(b);
          });
        }
        function onKey(e) {
          if (e.key === 'ArrowLeft') { e.preventDefault(); goPrev(); }
          else if (e.key === 'ArrowRight') { e.preventDefault(); goNext(); }
        }
        viewport.addEventListener('keydown', onKey);
        function sync() {
          var sel = embla.selectedScrollSnap();
          if (prev) prev.disabled = !embla.canScrollPrev();
          if (next) next.disabled = !embla.canScrollNext();
          for (var j = 0; j < dots.length; j++) {
            dots[j].classList.toggle('is-selected', j === sel);
            if (j === sel) dots[j].setAttribute('aria-current', 'true');
            else dots[j].removeAttribute('aria-current');
          }
        }
        embla.on('select', sync); embla.on('reInit', sync); sync();
        return function () {
          viewport.removeEventListener('keydown', onKey);
          if (prev) prev.removeEventListener('click', goPrev);
          if (next) next.removeEventListener('click', goNext);
          embla.destroy();
        };
      }
    },

    /* --------------------------- Motion (spring) ---------------------------- */

    'magnetic-button': {
      level: 'expressive', libs: ['motion'],
      run: function (el) {
        el.classList.add('motion-done');
        var M = win.Motion;
        var pull = isNaN(num(el, 'strength', NaN)) ? 0.28 : num(el, 'strength', 0.28);
        var spring = { type: 'spring', stiffness: 220, damping: 18, mass: 0.6 };
        function move(e) {
          var r = el.getBoundingClientRect();
          M.animate(el, {
            x: (e.clientX - (r.left + r.width / 2)) * pull,
            y: (e.clientY - (r.top + r.height / 2)) * pull
          }, spring);
        }
        function reset() { M.animate(el, { x: 0, y: 0 }, spring); }
        el.addEventListener('pointermove', move);
        el.addEventListener('pointerleave', reset);
        el.addEventListener('blur', reset);
        return function () {
          el.removeEventListener('pointermove', move);
          el.removeEventListener('pointerleave', reset);
          el.removeEventListener('blur', reset);
          el.style.transform = '';
        };
      }
    },

    /* ------------------- GSAP + ScrollTrigger / SplitText -------------------- */

    'split-headline': {
      level: 'expressive', libs: ['gsap', 'scrolltrigger', 'splittext'],
      run: function (el, o) {
        var gsap = win.gsap;
        var type = attr(el, 'split', 'lines');
        var tween = null, clearNet = null;
        el.classList.add('motion-done');
        // autoSplit re-splits once webfonts land and on resize. Building the tween inside onSplit
        // (and returning it) is the only way GSAP keeps it attached across those re-splits -
        // otherwise the hero headline ends up tweening elements that no longer exist.
        var split = new win.SplitText(el, {
          type: type, mask: 'lines', autoSplit: true, aria: 'auto',
          onSplit: function (self) {
            if (clearNet) clearNet();
            var targets = (type.indexOf('words') === -1 && self.lines && self.lines.length) ? self.lines : (self.words || self.lines);
            tween = gsap.from(targets, {
              yPercent: 115, opacity: 0, duration: o.duration / 1000, delay: o.delay / 1000,
              stagger: o.stagger / 1000, ease: o.ease,
              scrollTrigger: stConf(el, o, { once: true })
            });
            clearNet = failsafe(tween, targets, o.delay + o.duration + 1500);
            return tween;
          }
        });
        return function () {
          if (clearNet) clearNet();
          if (tween) { if (tween.scrollTrigger) tween.scrollTrigger.kill(); tween.kill(); }
          split.revert();
        };
      }
    },

    'image-mask-reveal': {
      level: 'expressive', libs: ['gsap', 'scrolltrigger'],
      run: function (el, o) {
        var gsap = win.gsap;
        var inner = el.querySelector('img, video, picture, .motion-media__inner') || el;
        el.classList.add('motion-done');
        var tl = gsap.timeline({ scrollTrigger: stConf(el, o, { once: true }) })
          .fromTo(el, { clipPath: 'inset(0% 0% 100% 0%)' },
                      { clipPath: 'inset(0% 0% 0% 0%)', duration: o.duration / 1000, delay: o.delay / 1000, ease: o.ease })
          .fromTo(inner, { scale: 1.12 }, { scale: 1, duration: (o.duration * 1.4) / 1000, ease: o.ease }, 0);
        var clearNet = failsafe(tl, [el, inner], o.delay + o.duration + 1500);
        return function () { clearNet(); if (tl.scrollTrigger) tl.scrollTrigger.kill(); tl.kill(); gsap.set([el, inner], { clearProps: 'all' }); };
      }
    },

    'parallax-media': {
      level: 'expressive', libs: ['gsap', 'scrolltrigger'],
      run: function (el, o) {
        var gsap = win.gsap;
        var inner = el.querySelector('img, video, picture, .motion-media__inner') || el;
        var shift = isNaN(o.strength) ? 8 : o.strength;
        el.classList.add('motion-done');
        var tween = gsap.fromTo(inner, { yPercent: -shift }, {
          yPercent: shift, ease: 'none',
          scrollTrigger: stConf(el, o, { start: 'top bottom', end: 'bottom top', scrub: o.scrubValue === false ? true : o.scrubValue })
        });
        return function () { if (tween.scrollTrigger) tween.scrollTrigger.kill(); tween.kill(); gsap.set(inner, { clearProps: 'all' }); };
      }
    },

    'sticky-progress': {
      level: 'expressive', libs: ['gsap', 'scrolltrigger'],
      run: function (el, o) {
        var gsap = win.gsap;
        var bar = el.querySelector('.motion-progress__bar') || el;
        var target = o.target ? doc.querySelector(o.target) : null;
        if (o.target && !target) { release(el); return function () {}; }
        // A trigger that IS the scroller never moves inside it: start and end collapse to the same
        // point and progress freezes. Drop it and use the scroller's own range instead.
        if (target && target === scroller) target = null;
        el.classList.add('motion-done');
        // With no explicit target, track the whole scroll range.
        var conf = target
          ? stConf(target, o, { start: 'top top', end: 'bottom bottom', scrub: o.scrubValue === false ? true : o.scrubValue })
          : { start: 0, end: 'max', scrub: o.scrubValue === false ? true : o.scrubValue };
        if (!target && scroller) conf.scroller = scroller;
        var tween = gsap.fromTo(bar, { scaleX: 0 }, {
          scaleX: 1, ease: 'none', transformOrigin: 'left center', scrollTrigger: conf
        });
        return function () { if (tween.scrollTrigger) tween.scrollTrigger.kill(); tween.kill(); gsap.set(bar, { clearProps: 'all' }); };
      }
    },

    'pin-scrub': {
      level: 'cinematic', libs: ['gsap', 'scrolltrigger'],
      run: function (el, o) {
        var gsap = win.gsap;
        var steps = el.querySelectorAll('[data-motion-step]');
        var dist = isNaN(o.distance) ? 100 : o.distance;
        el.classList.add('motion-done');
        var conf = stConf(el, o, {
          start: 'top top', end: '+=' + (win.innerHeight * (dist / 100) * Math.max(1, steps.length)),
          pin: true, scrub: o.scrubValue === false ? true : o.scrubValue, anticipatePin: 1
        });
        var tl = gsap.timeline({ scrollTrigger: conf });
        for (var i = 0; i < steps.length; i++) {
          if (i > 0) tl.fromTo(steps[i], { opacity: 0, yPercent: 8 }, { opacity: 1, yPercent: 0, ease: o.ease }, '>');
          if (i < steps.length - 1) tl.to(steps[i], { opacity: 0, yPercent: -8, ease: o.ease }, '>');
        }
        if (!steps.length) tl.fromTo(el.firstElementChild || el, { scale: 1 }, { scale: 1.06, ease: 'none' });
        return function () {
          if (tl.scrollTrigger) tl.scrollTrigger.kill();
          tl.kill();
          if (steps.length) gsap.set(steps, { clearProps: 'all' });
        };
      }
    },

    /* --------------------------- Lenis ---------------------------- */

    'smooth-scroll': {
      level: 'expressive', libs: ['lenis'],
      run: function (el, o) {
        el.classList.add('motion-done');
        if (lenis) return function () {};
        var conf = { duration: (o.duration * 2) / 1000, smoothWheel: true };
        // content === wrapper on purpose: Horizon's .page-wrapper has several element children
        // (header group, main, footer group), so its first child is not the scrollable content.
        // The wrapper's own scrollHeight is the correct scroll limit.
        if (scroller) { conf.wrapper = scroller; conf.content = scroller; }
        lenis = new win.Lenis(conf);
        var raf = 0;
        if (win.gsap && win.ScrollTrigger) {
          lenis.on('scroll', win.ScrollTrigger.update);
          win.gsap.ticker.add(lenisTick);
          win.gsap.ticker.lagSmoothing(0);
        } else {
          raf = win.requestAnimationFrame(function loop(t) { if (lenis) { lenis.raf(t); raf = win.requestAnimationFrame(loop); } });
        }
        return function () {
          if (win.gsap) win.gsap.ticker.remove(lenisTick);
          if (raf) win.cancelAnimationFrame(raf);
          if (lenis) { lenis.destroy(); lenis = null; }
        };
      }
    }
  };

  var lenis = null;
  var lastAtc = null;
  function lenisTick(t) { if (lenis) lenis.raf(t * 1000); }

  /* -------------------------------------------------------------- lifecycle */

  function setupElement(el) {
    if (bound.has(el)) return;
    bound.add(el);
    var name = resolve(el.getAttribute('data-motion'));
    if (!name) { release(el); return; }
    var def = PATTERNS[name];
    var o = opts(el);
    var root = el.closest('[data-section]') || doc.body;
    libs(def.libs).then(function () {
      var kill = def.run(el, o);
      register(root, typeof kill === 'function' ? kill : function () {});
    })['catch'](function (err) {
      if (win.console && win.console.warn) win.console.warn('[motion] ' + name + ':', err.message);
      release(el); // a failed CDN load must never leave content hidden
    });
  }

  function initRoot(root) {
    if (!root || reduced()) return;
    var nodes = [];
    if (root.nodeType === 1 && root.hasAttribute('data-motion')) nodes.push(root);
    var found = root.querySelectorAll ? root.querySelectorAll('[data-motion]') : [];
    for (var i = 0; i < found.length; i++) nodes.push(found[i]);

    // Cinematic: elements inside the hero auto-sequence unless the markup sets its own delay.
    if (level() === 'cinematic') {
      var heroes = root.querySelectorAll ? root.querySelectorAll('[data-section="hero"]') : [];
      for (var h = 0; h < heroes.length; h++) {
        var hn = heroes[h].querySelectorAll('[data-motion]');
        for (var k = 0; k < hn.length; k++) hn[k].__smAutoDelay = 120 + k * 90;
      }
    }
    for (var n = 0; n < nodes.length; n++) setupElement(nodes[n]);
  }

  function destroyRoot(root) {
    for (var i = instances.length - 1; i >= 0; i--) {
      if (root && instances[i].root !== root && !(root.contains && root.contains(instances[i].root))) continue;
      var kills = instances[i].kills;
      for (var k = 0; k < kills.length; k++) { try { kills[k](); } catch (e) {} }
      instances.splice(i, 1);
    }
    var els = root && root.querySelectorAll ? root.querySelectorAll('[data-motion]') : [];
    for (var j = 0; j < els.length; j++) { bound['delete'] && bound['delete'](els[j]); els[j].style.transition = ''; }
  }

  function destroy() {
    for (var i = instances.length - 1; i >= 0; i--) {
      var kills = instances[i].kills;
      for (var k = 0; k < kills.length; k++) { try { kills[k](); } catch (e) {} }
    }
    instances.length = 0;
    if (lenis) { lenis.destroy(); lenis = null; }
    var els = doc.querySelectorAll('[data-motion]');
    for (var j = 0; j < els.length; j++) {
      bound['delete'] && bound['delete'](els[j]);
      els[j].style.transition = '';
      els[j].__smAutoDelay = 0; // otherwise a cinematic hero keeps its delays after a level change
      els[j].classList.remove('motion-done');
    }
    booted = false;
  }

  // Remove every hidden state. Called under reduced motion and if boot never completes.
  function unlock() {
    HTML.classList.remove('motion-ready');
    var els = doc.querySelectorAll('[data-motion]');
    for (var i = 0; i < els.length; i++) release(els[i]);
  }

  function boot() {
    if (reduced()) { unlock(); return; }
    HTML.classList.add('motion-ready');
    try {
      scroller = resolveScroller(); // a bad data-motion-scroller selector throws in querySelector
      initRoot(doc.body);
      booted = true;
    } catch (e) {
      if (win.console && win.console.warn) win.console.warn('[motion] boot failed:', e.message);
      unlock(); // never leave the page in the pre-hidden state
    }
  }

  function refresh() {
    destroy();
    HTML.classList.remove('motion-ready');
    boot();
    if (win.ScrollTrigger) win.ScrollTrigger.refresh();
  }

  /* ----------------------------------------------------------------- wiring */

  win.setTimeout(function () { if (!booted) unlock(); }, BOOT_TIMEOUT);

  if (win.matchMedia) {
    var mq = win.matchMedia('(prefers-reduced-motion: reduce)');
    var onMq = function () { if (mq.matches) { destroy(); unlock(); } else { boot(); } };
    if (mq.addEventListener) mq.addEventListener('change', onMq);
    else if (mq.addListener) mq.addListener(onMq);
  }

  // The resolved scroller flips at Horizon's 990px breakpoint - rebuild when it changes.
  var resizeTimer = 0;
  win.addEventListener('resize', function () {
    win.clearTimeout(resizeTimer);
    resizeTimer = win.setTimeout(function () {
      if (!booted) return;
      if (resolveScroller() !== scroller) refresh();
      else if (win.ScrollTrigger) win.ScrollTrigger.refresh();
    }, 250);
  });

  // Shopify theme editor: re-init only the section that changed, and clean up on removal.
  doc.addEventListener('shopify:section:load', function (e) {
    if (!booted) return;
    initRoot(e.target);
    if (win.ScrollTrigger) win.ScrollTrigger.refresh(); // the new section changed every start/end
  });
  doc.addEventListener('shopify:section:unload', function (e) { destroyRoot(e.target); });

  win.StoreMotion = {
    version: VERSION,
    init: initRoot,
    destroy: destroy,
    refresh: refresh,
    level: level,
    scroller: function () { return scroller; },
    patterns: Object.keys(PATTERNS)
  };

  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', boot);
  else boot();

})(window, document);
